import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { DateRange } from "@/hooks/useDateRange";
import { useRegisterAnalyticsTabExport } from "@/components/pages/analytics/front-desk/context/AnalyticsTabExportContext";
import { toAnalyticsDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { formatAnalyticsDateRangeLabel } from "@/components/pages/analytics/front-desk/components/shared/analyticsFormatting";
import { getAdminTabMetricColumns } from "../config/adminTabMetricColumns";
import type { AdminHotelOverviewRow } from "../types/adminOverview";
import type { AdminMetricCell } from "../types/adminHotelMetricTable";
import { mapOverviewRowsToMetricRows } from "../utils/mapOverviewRowToMetricRow";
import {
  buildAdminHotelsMetricExportSection,
  buildAdminReportContextSection,
  downloadAdminHotelsMetricExport,
} from "../utils/adminTabExport";

type UseAdminOverviewTableExportParams = {
  dateRange: DateRange;
  tenantName: string;
  rows: AdminHotelOverviewRow[];
  networkMetrics: Record<string, AdminMetricCell> | null;
  canExport: boolean;
};

export function useAdminOverviewTableExport({
  dateRange,
  tenantName,
  rows,
  networkMetrics,
  canExport,
}: UseAdminOverviewTableExportParams): void {
  const { t } = useTranslation();

  const handleExport = useCallback(
    async (format: "csv" | "xlsx") => {
      const from = toAnalyticsDateParam(dateRange.from);
      const to = toAnalyticsDateParam(dateRange.to);

      if (
        !from ||
        !to ||
        !tenantName ||
        !networkMetrics ||
        !canExport ||
        rows.length === 0
      ) {
        return;
      }

      try {
        const columns = getAdminTabMetricColumns("overview");
        const metricRows = mapOverviewRowsToMetricRows(rows);
        const reportType = t("analytics.tabs.overview", "Overview");
        const reportName = t(
          "analytics.admin.export.tenantAnalytics",
          "Tenant Analytics",
        );
        const period = formatAnalyticsDateRangeLabel(
          dateRange,
          `${from} - ${to}`,
        );
        const appliedFilters = t(
          "analytics.admin.export.dateRangeFilter",
          "Date range: {{period}}",
          { period },
        );
        const contextSection = buildAdminReportContextSection(
          {
            reportName,
            reportType,
            tenantName,
            period,
            appliedFilters,
          },
          t,
        );
        const networkTotalRow = {
          id: "tenant-network-total",
          hotelBrand: t(
            "analytics.admin.export.networkTotal",
            "Hotel network total",
          ),
          hotelLogoId: null,
          metrics: networkMetrics,
        };
        const section = buildAdminHotelsMetricExportSection(
          reportType,
          columns,
          metricRows,
          t,
          [networkTotalRow],
        );

        downloadAdminHotelsMetricExport({
          format,
          filenameBase: `tenant-overview-${from}-${to}`,
          sections: [contextSection, section],
        });

        toast.success(
          t("analytics.admin.export.success", "Analytics export downloaded"),
        );
      } catch {
        toast.error(
          t("analytics.admin.export.error", "Failed to export analytics"),
        );
      }
    },
    [canExport, dateRange, networkMetrics, rows, t, tenantName],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport:
        canExport &&
        Boolean(tenantName) &&
        Boolean(networkMetrics) &&
        rows.length > 0,
      onExport: handleExport,
    }),
    [canExport, handleExport, networkMetrics, rows.length, tenantName],
  );

  useRegisterAnalyticsTabExport(exportRegistration);
}
